import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, PhoneCall } from "lucide-react";
import { PHONE, PHONE_TEL } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const QUICK_PROMPTS = ["What are your hours?", "Emergency service?", "How do I get a quote?"];

const inputCls =
  "w-full bg-[#0A0A0C] border border-gold/20 rounded-sm px-3.5 py-2.5 text-sm text-[#F4EFE6] placeholder:text-zinc-600 focus:outline-none focus:border-gold/60 transition-colors";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Hello, and welcome — I'm Faith, the Redeemer Solutions assistant. Ask me about our services, hours, or emergency availability, and I'll get you a clear answer. For a real person, tap \"Have us call you\" below.",
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [showCallForm, setShowCallForm] = useState(false);
  const [callForm, setCallForm] = useState({ name: "", phone: "", message: "" });
  const [callSending, setCallSending] = useState(false);
  const [callSent, setCallSent] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  if (!sessionRef.current) {
    let id = null;
    try {
      id = localStorage.getItem("rs_chat_session");
      if (!id) {
        id = crypto.randomUUID();
        localStorage.setItem("rs_chat_session", id);
      }
    } catch {
      id = `anon-${Date.now()}`;
    }
    sessionRef.current = id;
  }

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, showCallForm]);

  const patchLast = (text) =>
    setMessages((m) => {
      const copy = [...m];
      copy[copy.length - 1] = { role: "assistant", text };
      return copy;
    });

  const send = async (preset) => {
    const msg = (preset ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setSending(true);
    setShowCallForm(false);
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: msg }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6);
          if (data === "[DONE]") continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.delta) {
              acc += parsed.delta;
              patchLast(acc);
            } else if (parsed.error) {
              acc = parsed.error;
              patchLast(acc);
            }
          } catch {}
        }
      }
      if (!acc) patchLast(`I apologize — I'm having a little trouble answering just now. Please call ${PHONE} and we'll take care of you.`);
    } catch {
      patchLast(`I apologize — I'm having trouble connecting just now. Please call ${PHONE} and we'll take care of you.`);
    } finally {
      setSending(false);
    }
  };

  const sendCallback = async (e) => {
    e.preventDefault();
    setCallSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: callForm.name,
          email: "callback-request@redeemersolutionsllc.com",
          phone: callForm.phone,
          message: `[Chat callback request] ${callForm.message || "Please call me back."}`,
        }),
      });
      if (!res.ok) throw new Error("failed");
      setCallSent(true);
    } catch {
      setCallSent(false);
      setMessages((m) => [...m, { role: "assistant", text: `That didn't go through — please just call ${PHONE} and we'll take care of you.` }]);
      setShowCallForm(false);
    } finally {
      setCallSending(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-launcher-button"
        onClick={() => setOpen(!open)}
        aria-label={open ? "Close chat" : "Open chat assistant"}
        className="fixed z-[70] right-4 sm:right-6 bottom-[84px] md:bottom-6 w-14 h-14 rounded-full bg-gradient-to-br from-[#E6CA65] via-[#D4AF37] to-[#AA771C] text-[#0A0A0C] flex items-center justify-center shadow-[0_0_26px_rgba(212,175,55,0.45)] transition-transform duration-300 hover:scale-110"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed z-[70] right-4 sm:right-6 bottom-[152px] md:bottom-24 w-[calc(100vw-2rem)] max-w-sm h-[480px] bg-[#0D0D10] border border-gold/25 rounded-md shadow-[0_20px_60px_rgba(0,0,0,0.6)] flex flex-col overflow-hidden"
        >
          <div className="flex items-center gap-3 px-5 py-4 border-b border-gold/20 bg-[#121216]">
            <img src="/logo.png" alt="Redeemer Solutions" className="h-8 w-auto" />
            <div>
              <p className="font-display text-sm font-semibold text-[#F4EFE6]">Faith</p>
              <p className="font-mono-label text-[9px] uppercase tracking-[0.22em] text-gold">There's always a solution.</p>
            </div>
          </div>

          <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} data-testid={`chat-message-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed rounded-md ${
                    m.role === "user"
                      ? "bg-gradient-to-br from-[#E6CA65] via-[#D4AF37] to-[#AA771C] text-[#0A0A0C] font-medium"
                      : "bg-[#1A1A22] border border-gold/15 text-zinc-200"
                  }`}
                >
                  {m.text || (sending ? "…" : "")}
                </div>
              </div>
            ))}
            {showCallForm && !callSent && (
              <form data-testid="chat-callback-form" onSubmit={sendCallback} className="border border-gold/25 bg-[#121216] rounded-sm p-4 space-y-2.5">
                <p className="font-mono-label text-[10px] uppercase tracking-[0.2em] text-gold">Have us call you</p>
                <input required data-testid="chat-callback-name" placeholder="Your name" value={callForm.name} onChange={(e) => setCallForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} />
                <input required type="tel" data-testid="chat-callback-phone" placeholder="Phone number" value={callForm.phone} onChange={(e) => setCallForm((f) => ({ ...f, phone: e.target.value }))} className={inputCls} />
                <input data-testid="chat-callback-topic" placeholder="What's it about? (optional)" value={callForm.message} onChange={(e) => setCallForm((f) => ({ ...f, message: e.target.value }))} className={inputCls} />
                <button
                  type="submit"
                  data-testid="chat-callback-submit"
                  disabled={callSending}
                  className="w-full bg-gradient-to-br from-[#E6CA65] via-[#D4AF37] to-[#AA771C] text-[#0A0A0C] font-semibold text-sm py-2.5 rounded-sm disabled:opacity-60"
                >
                  {callSending ? "Sending..." : "Request a Call Back"}
                </button>
              </form>
            )}
            {callSent && (
              <div data-testid="chat-callback-success" className="border border-gold/25 bg-gold/5 rounded-sm p-4 text-sm text-zinc-200">
                Done — Zach will call you back. For anything urgent, call <a href={PHONE_TEL} className="text-gold">{PHONE}</a> now.
              </div>
            )}
          </div>

          <div className="px-4 pt-2 pb-1 flex flex-wrap gap-2">
            {QUICK_PROMPTS.map((q) => (
              <button
                key={q}
                data-testid={`chat-quick-${q.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => send(q)}
                disabled={sending}
                className="text-[11px] font-mono-label uppercase tracking-[0.12em] border border-gold/25 text-gold-champagne/90 px-2.5 py-1.5 rounded-full hover:bg-gold/10 transition-colors disabled:opacity-50"
              >
                {q}
              </button>
            ))}
            <button
              data-testid="chat-callback-toggle"
              onClick={() => { setShowCallForm(!showCallForm); setCallSent(false); }}
              className="text-[11px] font-mono-label uppercase tracking-[0.12em] border border-red-500/40 text-red-400 px-2.5 py-1.5 rounded-full hover:bg-red-500/10 transition-colors inline-flex items-center gap-1.5"
            >
              <PhoneCall size={11} /> Have us call you
            </button>
          </div>

          <form
            className="flex items-center gap-2 p-3 border-t border-gold/15"
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
          >
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about services, hours, pricing..."
              className={inputCls}
            />
            <button
              type="submit"
              data-testid="chat-send-button"
              disabled={sending || !input.trim()}
              aria-label="Send message"
              className="shrink-0 w-10 h-10 flex items-center justify-center bg-gradient-to-br from-[#E6CA65] via-[#D4AF37] to-[#AA771C] text-[#0A0A0C] rounded-sm transition-transform hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
